<template>
  <z-card shadow="never" :class="ns.b()">
    <template v-if="task">
      <div :class="ns.e('header')">
        <h4 :class="ns.e('title')">{{ task.title }}</h4>
        <div :class="ns.e('tags')">
          <z-tag :type="priorityTag.type" size="small" effect="plain">
            {{ priorityTag.label }}
          </z-tag>
          <z-tag :type="statusTag.type" size="small">
            {{ statusTag.label }}
          </z-tag>
        </div>
      </div>

      <p v-if="task.description" :class="ns.e('desc')">
        {{ task.description }}
      </p>

      <dl :class="ns.e('info')">
        <div>
          <dt>来源模块</dt>
          <dd>{{ task.module }}</dd>
        </div>
        <div>
          <dt>业务类型</dt>
          <dd>{{ task.bizType }}</dd>
        </div>
        <div>
          <dt>处理人</dt>
          <dd>{{ task.assignee }}</dd>
        </div>
        <div>
          <dt>创建人</dt>
          <dd>{{ task.creator }}</dd>
        </div>
        <div v-if="task.dueTime">
          <dt>截止时间</dt>
          <dd>{{ formatTime(task.dueTime) }}</dd>
        </div>
        <div>
          <dt>创建时间</dt>
          <dd>{{ formatTime(task.createTime) }}</dd>
        </div>
      </dl>

      <div :class="ns.e('actions')">
        <z-button
          v-if="task.status === 'pending' || task.status === 'overdue'"
          type="primary"
          size="small"
          @click="emit('start', task.id)"
        >
          开始处理
        </z-button>
        <z-button
          v-if="task.status === 'processing'"
          type="success"
          size="small"
          @click="emit('complete', task.id)"
        >
          标记完成
        </z-button>
        <z-button v-if="task.bizTab" size="small" @click="emit('open', task)">
          打开关联业务
        </z-button>
        <z-button
          v-if="task.status !== 'done' && task.status !== 'cancelled'"
          size="small"
          @click="emit('ignore', task.id)"
        >
          忽略
        </z-button>
      </div>

      <h5 :class="ns.e('timeline-title')">操作记录</h5>
      <z-timeline v-if="logs.length">
        <z-timeline-item
          v-for="(log, index) in logs"
          :key="index"
          :timestamp="formatTime(log.time)"
          placement="top"
        >
          <strong>{{ log.operator }}</strong> {{ log.action }}
          <span v-if="log.remark" :class="ns.e('remark')"
            >（{{ log.remark }}）</span
          >
        </z-timeline-item>
      </z-timeline>
      <z-empty v-else description="暂无操作记录" />
    </template>

    <z-empty v-else description="请选择左侧任务查看详情" />
  </z-card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useNamespace } from '@zoryn/hooks'
import ZButton from '@zoryn/components/button'
import ZCard from '@zoryn/components/card'
import ZEmpty from '@zoryn/components/empty'
import ZTag from '@zoryn/components/tag'
import ZTimeline, { ZTimelineItem } from '@zoryn/components/timeline'
import {
  PRIORITY_TAG_MAP,
  STATUS_TAG_MAP,
  type TaskActionLog,
  type TaskRecord,
} from '../types'

const props = defineProps<{
  task: TaskRecord | null
  logs: TaskActionLog[]
}>()

const emit = defineEmits<{
  start: [id: string]
  complete: [id: string]
  ignore: [id: string]
  open: [task: TaskRecord]
}>()

const ns = useNamespace('task-center-detail')

const statusTag = computed(() =>
  props.task ? STATUS_TAG_MAP[props.task.status] : STATUS_TAG_MAP.pending
)
const priorityTag = computed(() =>
  props.task ? PRIORITY_TAG_MAP[props.task.priority] : PRIORITY_TAG_MAP.normal
)

function formatTime(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}
</script>
